import { useQuery } from '@tanstack/react-query';
import { api } from '../api.ts';

/**
 * Progressive-disclosure gate — read what surfaces to render for this org (and
 * optionally this site) from a single bundle (`docs/progressive-disclosure.md`).
 *
 * ## Why one bundle
 *
 * Every enterprise-oriented screen — ECO, Sweep board, warden board, off-site
 * notices, portfolio header, venue screen — is wrapped in
 * `{isVisible('…') && <Screen />}`. Rather than one HTTP round trip per gate,
 * the endpoint returns the whole map (sixteen booleans today) and every gate
 * reads it out of TanStack Query's cache. The set of surfaces grows a row at
 * a time and never changes within a session, so caching is trivial.
 *
 * ## Phase 1 (this module)
 *
 * The endpoint answers `true` for every surface, so `isVisible(name)` returns
 * `true` for every existing org and this hook is a no-op from a rendering
 * perspective — the invariant that lets Phase 1 ship without a single UX
 * regression.
 *
 * ## Phase 2 (the follow-up)
 *
 * The server's trigger evaluation becomes real. This hook does *not* change:
 * the client still reads a single bundle. Cache invalidation on membership /
 * site / zone / eco_assignments writes is Phase 2's problem and gets wired
 * from the mutations that already exist.
 *
 * ## The loading return
 *
 * `isVisible(name)` returns `true` while the query is loading. A flash of
 * hidden content on every screen — the ECO screen briefly disappearing on
 * navigation to a page that also renders it — would be worse than the
 * hypothetical case a Phase 2 site glimpses a surface for a heartbeat before
 * it is hidden. The design doc names the always-appears surfaces (`Raise` in
 * the chrome, personal-app screens) and this hook only gates the ones that
 * are *sometimes* hidden.
 *
 * A hard failure (401, network) also returns `true`. The endpoint reads
 * membership, and if the caller cannot reach it the shell has bigger
 * problems than which sub-surface to draw — refusing to render anything
 * because the disclosure fetch failed would take the whole dashboard down.
 */

export type DisclosureBundle = {
  surfaces: Record<string, boolean>;
};

async function fetchDisclosure(orgId: string, siteId?: string): Promise<DisclosureBundle> {
  const suffix = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
  const value = await api<DisclosureBundle>(`/orgs/${orgId}/disclosure${suffix}`);
  return value ?? { surfaces: {} };
}

/**
 * The hook. `siteId` is optional — some surfaces are per-site (a Sweep board
 * is a fact about *one* site, not the org), so a screen that already knows
 * which site it is on passes that in. Phase 1 ignores it; Phase 2 needs it.
 *
 * The query key includes `siteId` so a per-site cache miss forces a fetch
 * rather than serving the org-level answer, which will start being wrong the
 * moment Phase 2 lands.
 */
export function useDisclosure(orgId: string | undefined, siteId?: string) {
  const q = useQuery({
    queryKey: ['disclosure', orgId ?? null, siteId ?? null],
    queryFn: () => fetchDisclosure(orgId!, siteId),
    enabled: Boolean(orgId),
    // The bundle changes on membership / site / zone / eco_assignments
    // writes and nothing else, so a short polling refetch would be waste.
    // Phase 2's invalidation is triggered by the mutations that move a
    // trigger, not by a timer.
    staleTime: 5 * 60_000,
  });

  return {
    surfaces: q.data?.surfaces ?? {},
    isLoading: q.isLoading,
    /**
     * Should this surface render for the current org (and site, if given)?
     *
     * Returns `true` while the query is loading and on error, deliberately —
     * see the module comment. In Phase 1 this returns `true` for every
     * surface the server knows about, which is every enterprise surface.
     */
    isVisible: (name: string): boolean => {
      if (q.isLoading || q.isError || !q.data) return true;
      // An unknown surface answers `false` at the server. This mirrors that
      // rather than defaulting to `true`, so a client typo reads as "not
      // there" rather than as "yes by default" — the same invariant the
      // module in api enforces.
      if (!(name in q.data.surfaces)) return false;
      return q.data.surfaces[name] === true;
    },
  };
}
